import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Printer, Info } from "lucide-react";
import { Input } from "../../components/ui/input2";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateSettings } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

const PrinterSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const [isDirectPrint, setIsDirectPrint] = useState(false);
  const [printerName, setPrinterName] = useState("");
  const [receiptSize, setReceiptSize] = useState("80mm");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (settingsData && settingsData.settings) {
      setIsDirectPrint(settingsData.settings.print_receipt === "active");
      setPrinterName(settingsData.settings.printer_name || "");
      setReceiptSize(settingsData.settings.receipt_format || "80mm");
    }
  }, [settingsData]);

  const handleSave = async () => {
    if (!settingsData?.settings) return;
    setSaving(true);

    try {
      const s = settingsData.settings;
      const payload = {
        company_name: s.company_name,
        company_email: s.company_email,
        company_phone: s.company_phone,
        company_phone_2: s.company_phone_2 || "",
        landline: s.landline || "",
        address: s.address,
        website: s.website,
        date_format: s.date_format,
        time_format: s.time_format,
        currency_id: Number(s.currency_id),
        currency_code: "",
        currency_symbol: "",
        timezone: s.timezone,
        locale: s.locale,
        receipt_format: receiptSize,
        vkb_is_visible: s.vkb_is_visible ? 1 : 0,
        vkb_position: s.vkb_position,
        show_contact_details: s.show_contact_details ? 1 : 0,
        print_receipt: isDirectPrint ? "active" : "deactive",
        printer_name: printerName,
        is_sunbed: Number(s.is_sunbed),
        is_sunbed_tmax: Number(s.is_sunbed_tmax)
      };

      console.log("Updating printer settings with printer_name:", printerName);
      console.log("Full payload:", payload);


      await updateSettings(s.id, payload as any);
      toast.success("Printer settings updated successfully!");
      refreshSettings();
    } catch (error: any) {
      console.error("Failed to update printer settings", error);
      toast.error("Failed to update printer settings. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading printer settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Printer Settings"
        description="Configure printer and receipt settings"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Printer size={18} className="text-white" />
            Printer Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            {/* Printer Settings Section */}
            <div>
              <SectionHeader title="Printer Settings" />

              {/* Info Box */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Configure your receipt printer settings for direct printing
                </p>
              </div>

              <div className="space-y-4">
                {/* Direct Print Toggle */}
                <ToggleSetting
                  label="Direct Print"
                  description="Enable direct printing without print dialog"
                  checked={isDirectPrint}
                  onChange={setIsDirectPrint}
                />

                {/* Printer Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Printer Name
                  </label>
                  <Input
                    type="text"
                    value={printerName}
                    onChange={(e) => setPrinterName(e.target.value)}
                    placeholder="Enter printer name"
                  />
                  <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                    <Info size={12} />
                    Enter the exact name of your receipt printer
                  </p>
                </div>

                {/* Receipt Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Print Receipt Size
                  </label>
                  <Select value={receiptSize} onValueChange={setReceiptSize}>
                    <SelectTrigger className="rounded-lg bg-gray-100 text-sm text-gray-900 shadow-sm transition-all duration-150 ease-in-out border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40 hover:bg-gray-50 focus:bg-white">
                      <SelectValue placeholder="Select receipt size" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="80mm">80mm</SelectItem>
                      <SelectItem value="58mm">58mm</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                    <Info size={12} />
                    Select the paper width for your receipt printer
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterSettings;
